import { Route, RouteConfig } from 'vue-router';
import { canIViewFeature } from '@/plugins/feature-toggle';

// @ts-expect-error:
import customerDataService from '@platform/customer-data-service';

const RBAC_FEATURE = 'zcc-rbac';
const RBAC_MODULE = 'cfg';
const VIEW_PERMISSION = 'view';

export const ZCC_RBAC_RESOURCES = {
  plans: 'plans',
  financial: 'fin',
} as const;

export type ZccRbacResource = typeof ZCC_RBAC_RESOURCES[keyof typeof ZCC_RBAC_RESOURCES];

type PermissionsByResource = Record<string, string[] | undefined>;
type PermissionsByModule = Record<string, PermissionsByResource | undefined>;

type CustomerDataService = {
  getAllPermissions: () => Promise<PermissionsByModule>;
};

function getRouteMeta(route: { meta?: unknown }) {
  return route.meta as { zccRbacResource?: ZccRbacResource } | undefined;
}

async function isRbacEnabled(): Promise<boolean> {
  return canIViewFeature({ name: RBAC_FEATURE });
}

async function canExecute(module: string, resource: string, action = VIEW_PERMISSION): Promise<boolean> {
  try {
    const service = customerDataService as CustomerDataService;
    const permissions = await service.getAllPermissions();

    return permissions[module]?.[resource]?.includes(action) ?? false;
  } catch {
    return false;
  }
}

async function canViewResource(resource: ZccRbacResource): Promise<boolean> {
  return canExecute(RBAC_MODULE, resource);
}

function getRouteResource(route: Route | RouteConfig): ZccRbacResource | undefined {
  const matched = 'matched' in route ? route.matched : undefined;
  const matchedResource = matched
    ?.slice()
    .reverse()
    .find((record) => getRouteMeta(record)?.zccRbacResource)
    ?.meta?.zccRbacResource;

  return matchedResource || getRouteMeta(route)?.zccRbacResource;
}

async function canViewRoute(route: Route | RouteConfig): Promise<boolean> {
  const resource = getRouteResource(route);

  if (!resource) {
    return true;
  }

  if (!(await isRbacEnabled())) {
    return true;
  }

  return canViewResource(resource);
}

export const zccRbacService = {
  isRbacEnabled,
  canExecute,
  canViewResource,
  getRouteResource,
  canViewRoute,
};
